/**
 * useApprovalsTabs — the data-loading hook that feeds the pure `Nav_Deriver`
 * (approval-rollup-surfaces, Req 1.6, 1.9, 5.2).
 *
 * The pure `visibleApprovalsTabs(flag, pages, hasCapability)` selector in
 * `reconciliation/navConsolidation.ts` derives the ordered Approvals tab set
 * from the Active_Form's approval-requiring pages, but it needs those pages,
 * which require async reads. This hook owns that I/O and feeds the pure deriver,
 * returning `{ tabs, loading }` for the nav consumers (`AdminSidebar`,
 * `SettingsPage`).
 *
 * Resolution mirrors `ApprovedTitlesSurface` / `ApplicationReviewSurface`:
 *   - `competitionId` is resolved from `useActiveSeason().operativeYear` →
 *     `resolveActiveCompetitionId(operativeYear)` when not supplied (the same
 *     single seeded `comp-${year}-idaho-america` binding those surfaces use).
 *   - The Active_Form is read via `getApplicationForm(competitionId)` then its
 *     working pages via `listFormPages(form.id)`, all through the
 *     `applicationFormClient` transport seam (never AppSync directly) so the
 *     hook flips mock↔live with the rest of the review methods.
 *
 * Fail-safe degradation (Req 1.9, 1.6): a null form (no published Active_Form)
 * or any read failure degrades to an EMPTY page list, so `visibleApprovalsTabs`
 * returns only the constant, retained Applications entry — never a stale or
 * partial derived set. The flag + capability gate is enforced by the pure
 * deriver: derived Rollup_Tabs appear only when `isApplicationFormEnabled()` AND
 * the caller holds `canManageUsers` (Req 5.2).
 */

import { useEffect, useState } from 'react';

import { applicationFormClient } from '@/lib/application-form/client';
import type { FormPage } from '@/lib/application-form/client/types';
import {
  visibleApprovalsTabs,
  type ApprovalsEntry,
} from '@/lib/application-form/reconciliation/navConsolidation';
import { isApplicationFormEnabled } from '@/config/featureFlags';
import { resolveActiveCompetitionId } from '@/lib/application-form/activeCompetition';
import { useActiveSeason } from '@/hooks/useActiveSeason';
import { useAuth } from '@/hooks/useAuth';

/** The state returned by {@link useApprovalsTabs}. */
export interface UseApprovalsTabsResult {
  /**
   * The flag- and capability-gated Approvals tab set: always the constant
   * Applications entry, plus one derived Rollup_Tab per approval-requiring page
   * when the Application_Form_Flag is enabled AND the user holds
   * `canManageUsers`.
   */
  tabs: readonly ApprovalsEntry[];
  /** True until the Active_Form's pages have resolved (or failed) once. */
  loading: boolean;
}

/**
 * Load the Active_Form's pages and derive the visible Approvals tab set.
 *
 * @param competitionId optional explicit competition; resolved from the active
 *   season when omitted (the same binding as `ApprovedTitlesSurface`).
 */
export function useApprovalsTabs(competitionId?: string): UseApprovalsTabsResult {
  const { operativeYear } = useActiveSeason();
  const { can, user } = useAuth();
  const resolvedCompetitionId =
    competitionId ?? resolveActiveCompetitionId(operativeYear, user?.activeTenantId);

  const [pages, setPages] = useState<readonly FormPage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const form = await applicationFormClient.getApplicationForm(resolvedCompetitionId);
        // A null form (no published Active_Form) degrades to an empty page list
        // → the Applications entry only (Req 1.9, 1.6).
        if (form == null || form.id == null || form.id === '') {
          if (!cancelled) setPages([]);
          return;
        }
        const formPages = await applicationFormClient.listFormPages(form.id);
        if (!cancelled) setPages(formPages ?? []);
      } catch {
        // Any read failure degrades to the constant Applications entry only
        // (Req 1.9, 1.6) — never a stale or partial derived set.
        if (!cancelled) setPages([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [resolvedCompetitionId]);

  // Apply the pure, flag- and capability-gated deriver (Req 5.2). Recomputed on
  // every render so a flag or capability change is reflected without re-reading.
  const tabs = visibleApprovalsTabs(isApplicationFormEnabled(), pages, can);

  return { tabs, loading };
}

export default useApprovalsTabs;
